import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type AnchorHTMLAttributes,
  type ReactNode,
} from "react";

type RouterValue = {
  path: string;
  navigate: (to: string | number, replace?: boolean) => void;
};
const RouterContext = createContext<RouterValue | null>(null);

export function BrowserRouter({ children }: { children: ReactNode }) {
  const [path, setPath] = useState(window.location.pathname);
  useEffect(() => {
    const listener = () => setPath(window.location.pathname);
    window.addEventListener("popstate", listener);
    return () => window.removeEventListener("popstate", listener);
  }, []);
  const value = useMemo<RouterValue>(
    () => ({
      path,
      navigate: (to, replace = false) => {
        if (typeof to === "number") {
          window.history.go(to);
          return;
        }
        window.history[replace ? "replaceState" : "pushState"]({}, "", to);
        setPath(window.location.pathname);
        window.scrollTo({ top: 0, behavior: "instant" });
      },
    }),
    [path],
  );
  return (
    <RouterContext.Provider value={value}>{children}</RouterContext.Provider>
  );
}

export function useRouter() {
  const context = useContext(RouterContext);
  if (!context) throw new Error("BrowserRouter is missing");
  return context;
}
export function useNavigate() {
  return useRouter().navigate;
}
export function useParams() {
  const { path } = useRouter();
  return {
    signalId: path.startsWith("/signals/")
      ? decodeURIComponent(path.slice("/signals/".length))
      : undefined,
  };
}

type LinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & {
  to: string;
};
export function Link({ to, onClick, ...props }: LinkProps) {
  const { navigate } = useRouter();
  return (
    <a
      href={to}
      {...props}
      onClick={(event) => {
        onClick?.(event);
        if (
          !event.defaultPrevented &&
          event.button === 0 &&
          !event.metaKey &&
          !event.ctrlKey
        ) {
          event.preventDefault();
          navigate(to);
        }
      }}
    />
  );
}

type NavLinkProps = Omit<LinkProps, "className"> & {
  end?: boolean;
  className: string | ((state: { isActive: boolean }) => string);
};
export function NavLink({ end, className, to, ...props }: NavLinkProps) {
  const { path } = useRouter();
  const isActive = end ? path === to : path === to || path.startsWith(`${to}/`);
  return (
    <Link
      to={to}
      className={
        typeof className === "function" ? className({ isActive }) : className
      }
      {...props}
    />
  );
}
